import * as React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

import {
  getBookingLimits,
  getEntitlementLimits,
  updateBookingLimit,
  updateEntitlementLimit,
} from "@/api/client";
import { BookingTierLimitView, EntitlementTierLimitView } from "@/api/types";
import { PageTitle } from "@/components/PageTitle";
import { Stepper } from "@/components/Stepper";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

/**
 * Admin: tune the per-tier plan. THE single place limits are edited — the old
 * /admin/booking-limits page edited the same booking_tier_limit table with different labels and
 * has been deleted. Grouped by area: Availability patterns, Booking links, Add-ons. Two DB tables
 * back this (entitlement_tier_limit + booking_tier_limit); a Save writes both. Numbers use a
 * flywheel stepper; entitlement quotas allow ∞ (-1), booking caps are positive counts. Applies
 * with no deploy.
 *
 * <p>Rule for this page: a control only appears if changing it changes behaviour. A Save still
 * round-trips monthly_bookings and analytics so their stored values survive, but neither is
 * offered as a dial, because nothing reads them.</p>
 */
/**
 * Tier identity colours, left on the raw palette by the token migration for the same
 * reason as UpgradePage's TIER_META: the brand has four hues, there are four tiers,
 * and picking which gets which is a brand decision rather than a codemod's.
 *
 * ⚠️ This map and UpgradePage's DISAGREE. Here BASIC is brand-teal and PRO is
 * brand-blue; there BASIC is brand-blue and PRO is indigo/violet. The same tier is a
 * different colour depending on which page you are looking at, because the map is
 * duplicated rather than shared. Worth reconciling into one exported map when the
 * tier palette is settled.
 */
const TIER_STYLE: Record<string, { bar: string; badge: string }> = {
  FREE: { bar: "from-slate-300 to-slate-400", badge: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  BASIC: { bar: "from-brand-teal/70 to-brand-teal", badge: "bg-brand-teal/20 text-brand-teal dark:bg-brand-teal/20 dark:text-brand-teal" },
  PRO: { bar: "from-brand-blue/70 to-brand-blue", badge: "bg-brand-blue/20 text-brand-blue dark:bg-brand-blue/20 dark:text-brand-blue" },
  ENTERPRISE: { bar: "from-amber-300 to-orange-400", badge: "bg-amber-200 text-amber-900 dark:bg-amber-900 dark:text-amber-100" },
};
const ORDER = ["FREE", "BASIC", "PRO", "ENTERPRISE"];

type Combined = EntitlementTierLimitView & Omit<BookingTierLimitView, "tier">;

export function AdminPlansPage() {
  const [rows, setRows] = React.useState<Combined[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    Promise.all([getEntitlementLimits(), getBookingLimits()])
      .then(([ent, bk]) => {
        const byTier = new Map(bk.map((b) => [String(b.tier), b]));
        const merged = ent
          .map((e) => ({ ...e, ...stripTier(byTier.get(e.tier)) }))
          .sort((a, b) => ORDER.indexOf(a.tier) - ORDER.indexOf(b.tier));
        setRows(merged as Combined[]);
      })
      .catch(() => setError("Couldn't load the plans. Admin access required."));
  }, []);

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 font-comic-body">
      <header>
        <Link
          to="/admin"
          className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:underline"
        >
          ← back to admin
        </Link>
        <PageTitle className="mt-1">Plans &amp; limits</PageTitle>
        <p className="mt-1 text-sm text-muted-foreground">
          What each tier can do. Changes apply instantly, no deploy. A quota of ∞ is unlimited.
        </p>
      </header>

      {error && (
        <p className="rounded-xl border border-border bg-destructive/10 px-4 py-3 text-sm text-destructive-strong">
          {error}
        </p>
      )}

      {rows && (
        <div className="grid gap-5 md:grid-cols-2">
          {rows.map((row) => (
            <TierCard
              key={row.tier}
              row={row}
              onSaved={(updated) =>
                setRows((rs) => rs?.map((r) => (r.tier === updated.tier ? updated : r)) ?? null)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TierCard({ row, onSaved }: { row: Combined; onSaved: (r: Combined) => void }) {
  const [draft, setDraft] = React.useState(row);
  const [saving, setSaving] = React.useState(false);
  const [justSaved, setJustSaved] = React.useState(false);

  React.useEffect(() => setDraft(row), [row]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(row);
  const set = (patch: Partial<Combined>) => setDraft((d) => ({ ...d, ...patch }));
  const style = TIER_STYLE[row.tier] ?? TIER_STYLE.FREE;

  async function save() {
    setSaving(true);
    try {
      await Promise.all([
        updateEntitlementLimit(row.tier, {
          availability_patterns: draft.availability_patterns,
          monthly_bookings: draft.monthly_bookings,
          custom_branding: draft.custom_branding,
          analytics: draft.analytics,
          audit_log: draft.audit_log,
          ai_scheduling: draft.ai_scheduling,
          a2a_marketplace: draft.a2a_marketplace,
        }),
        updateBookingLimit(row.tier, {
          max_active_pages: draft.max_active_pages,
          max_active_rolling: draft.max_active_rolling,
          max_active_polls: draft.max_active_polls,
          max_adhoc_per_month: draft.max_adhoc_per_month,
          max_polls_per_month: draft.max_polls_per_month,
          max_bookings_per_page: draft.max_bookings_per_page,
          max_link_days: draft.max_link_days,
          max_ranges: draft.max_ranges,
        }),
      ]);
      onSaved(draft);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1600);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition hover:shadow-md">
      <div className={cn("h-1.5 bg-gradient-to-r", style.bar)} />
      <div className="p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold tracking-tight">{titleCase(row.tier)}</h2>
          <span className={cn("rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide", style.badge)}>
            Plan
          </span>
        </div>

        <SectionLabel>Availability patterns</SectionLabel>
        <div className="space-y-1">
          <NumRow label="Availability patterns" hint="Templates they invite with" value={draft.availability_patterns} onChange={(v) => set({ availability_patterns: v })} unlimited />
        </div>

        {/* Only dials that actually gate something belong here. "Bookings / month"
            (monthly_bookings) and "Analytics" were removed: both saved happily and changed
            nothing, which is the exact trap this page is being cleaned up to avoid. Their
            columns and capabilities are untouched, so either is one row away from returning
            the moment something enforces it. */}
        {/* Grouped by offering, because the pricing page sells them separately and they count
            separate tables. "Live ad-hoc links" is max_active_pages under its old name.
            Rolling links have no monthly cap at any tier, which is why that row is absent
            rather than set to something large. */}
        <SectionLabel>Live at a time</SectionLabel>
        <div className="space-y-1">
          <NumRow label="Live ad-hoc links" hint="Single-use links live at once, checked when they create one; existing links stay" value={draft.max_active_pages} onChange={(v) => set({ max_active_pages: v })} min={1} />
          <NumRow label="Live rolling links" hint="Long-lived links live at once" value={draft.max_active_rolling} onChange={(v) => set({ max_active_rolling: v })} min={1} />
          <NumRow label="Live poll links" hint="Polls open at once" value={draft.max_active_polls} onChange={(v) => set({ max_active_polls: v })} min={1} />
        </div>

        <SectionLabel>Created per month</SectionLabel>
        <div className="space-y-1">
          <NumRow label="Ad-hoc links / month" hint="Counted by creation date, so deleting one does not free the slot" value={draft.max_adhoc_per_month} onChange={(v) => set({ max_adhoc_per_month: v })} min={1} />
          <NumRow label="Poll links / month" hint="Counted by creation date, so deleting one does not free the slot" value={draft.max_polls_per_month} onChange={(v) => set({ max_polls_per_month: v })} min={1} />
        </div>

        <SectionLabel>Per link</SectionLabel>
        <div className="space-y-1">
          <NumRow label="Bookings per link" hint="How many a single link accepts before full" value={draft.max_bookings_per_page} onChange={(v) => set({ max_bookings_per_page: v })} min={1} />
          <NumRow label="Link expiry" hint="Days a booking link stays open" value={draft.max_link_days} onChange={(v) => set({ max_link_days: v })} min={1} max={3650} />
          <NumRow label="Ranges per link" hint="Time ranges drawn on a single link. Not the availability-pattern count above" value={draft.max_ranges} onChange={(v) => set({ max_ranges: v })} min={1} />
        </div>

        <SectionLabel>Add-ons</SectionLabel>
        <div className="space-y-1">
          <ToggleRow label="Custom branding" checked={draft.custom_branding} onChange={(v) => set({ custom_branding: v })} />
          <ToggleRow label="Audit log" checked={draft.audit_log} onChange={(v) => set({ audit_log: v })} />
          <ToggleRow label="Agent chat" checked={draft.ai_scheduling} onChange={(v) => set({ ai_scheduling: v })} />
          <ToggleRow label="Reachable by outside agents" checked={draft.a2a_marketplace} onChange={(v) => set({ a2a_marketplace: v })} />
        </div>

        <div className="mt-5 flex items-center justify-end gap-3">
          {justSaved && (
            <span className="flex items-center gap-1 text-xs font-medium text-success-strong">
              <Check className="h-3.5 w-3.5" /> Saved
            </span>
          )}
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={save}
            className="rounded-full bg-brand-blue px-5 py-2 font-display text-sm font-semibold text-brand-blue-foreground shadow-sm transition hover:bg-brand-blue/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 mt-5 font-display text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
      {children}
    </h3>
  );
}

function NumRow({
  label,
  hint,
  value,
  onChange,
  unlimited,
  min,
  max,
}: {
  label: string;
  hint: string;
  value: number;
  onChange: (n: number) => void;
  unlimited?: boolean;
  min?: number;
  max?: number;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl px-2 py-1.5 transition hover:bg-muted/40">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="truncate text-[11px] text-muted-foreground">{hint}</div>
      </div>
      <Stepper value={value} onChange={onChange} allowUnlimited={unlimited} min={min} max={max} ariaLabel={label} />
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl px-2 py-1.5 transition hover:bg-muted/40">
      <span className="text-sm font-medium">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function stripTier(b: BookingTierLimitView | undefined): Omit<BookingTierLimitView, "tier"> {
  return {
    // Fallbacks are Free's numbers throughout: if the booking row is missing we do not know what
    // this tier allows, and the safe placeholder to show an admin is the most restrictive one.
    max_active_pages: b?.max_active_pages ?? 1,
    max_active_rolling: b?.max_active_rolling ?? 1,
    max_active_polls: b?.max_active_polls ?? 1,
    max_adhoc_per_month: b?.max_adhoc_per_month ?? 2,
    max_polls_per_month: b?.max_polls_per_month ?? 2,
    max_bookings_per_page: b?.max_bookings_per_page ?? 1,
    max_link_days: b?.max_link_days ?? 7,
    max_ranges: b?.max_ranges ?? 1,
  };
}

function titleCase(tier: string): string {
  return tier.charAt(0) + tier.slice(1).toLowerCase();
}
